import React, { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import { Chart } from '@antv/g2';
import { ConfigProvider } from 'antd';
import { useTheme } from '../../theme/ThemeContext';
import Select from '../../atoms/Select/Select';
import Button from '../../atoms/Button/Button';
import DownloadFile from '../../atoms/Icon/DownloadFile';
import ArrowRight from '../../atoms/Icon/ArrowRight';
import { BrandPriceLadderProps } from './BrandPriceLadder.types';
import { ChartConfig, applyConfig } from '../../../utils/BaseChart/BaseChartOptions';
import { transformVizDataToBrandPriceLadder } from '../../../utils/transformVizData';
import {
    getAggregatedChartTooltipInteraction,
    getChartColumnHighlightInteraction,
} from '../../../utils/chartAggregatedTooltip';
import { formatNumWithKMB, formatNumWithKMBTwoDecimalPlaces, formatXAxisLabel, CHART_FONT_FAMILY_CSS } from '../../../utils/formatters';
import BrandPriceLadderChartOptions from './BrandPriceLadderChartOptions';
import ChartFilterBar, { ChartFilterColumn, CHART_FILTER_SELECT_CLASSNAME } from '../EnhancedBarChart/ChartFilterBar';
import { chartMultiSelectTagRender } from '../EnhancedBarChart/chartFilterMultiTagRender';

const BrandPriceLadder: React.FC<BrandPriceLadderProps> = (props) => {
    const {
        vizData,
        title: propTitle,
        description,
        filters = [],
        footerLabel,
        showFooterArrow = true,
        onDownload,
        onBarClick,
        onPointClick,
        scrollbarVisibleCount: scrollbarVisibleCountProp,
        sizeAttributes,
        viewLevelAdditionalProps,
        additionalMarks,
        className = '',
        chartClassName = '',
    } = props;

    const scrollbarVisibleCount = scrollbarVisibleCountProp ?? 10;

    const theme = useTheme();

    // X-axis filter state (same logic as EnhancedBarChart)
    const [xFilter, setXFilter] = useState<string[]>([]);

    // X-axis filter options from vizData (unique x values, same as bar chart)
    const xFilterOptions = useMemo(() => {
        if (!vizData?.data) return [];
        const xValues = vizData.data.flatMap((d) => (Array.isArray(d.x) ? d.x : d.x != null ? [String(d.x)] : []));
        return Array.from(new Set(xValues)).sort();
    }, [vizData]);

    // Filtered vizData based on x-axis selection (same logic as bar chart)
    const filteredVizData = useMemo(() => {
        if (!vizData) return undefined;
        if (xFilter.length === 0) return vizData;
        const filteredData = vizData.data.filter((dataPoint) => {
            const xValue = Array.isArray(dataPoint.x) ? dataPoint.x?.[0] : dataPoint.x;
            const str = xValue != null ? String(xValue) : '';
            return str && xFilter.includes(str);
        });
        return { ...vizData, data: filteredData };
    }, [vizData, xFilter]);

    // Transform viz_data if provided (use filtered data)
    const chartConfig = useMemo(() => {
        if (!filteredVizData) {
            throw new Error('vizData is required for BrandPriceLadder');
        }
        return transformVizDataToBrandPriceLadder(filteredVizData);
    }, [filteredVizData]);

    const { title, data, xAxisKey, salesBarConfig, pricePointConfigs, y1AxisLabel, y2AxisLabel } = chartConfig;

    const uniqueCategories = useMemo(
        () => Array.from(new Set(data.map((d: Record<string, unknown>) => d[xAxisKey]))),
        [data, xAxisKey],
    );
    const totalCategories = uniqueCategories.length;

    const [sliderRange, setSliderRange] = useState<[number, number]>([0, Math.max(0, totalCategories - 1)]);

    useEffect(() => {
        setSliderRange([0, Math.max(0, totalCategories - 1)]);
    }, [totalCategories]);

    const sliderFilteredCategories = useMemo(
        () => new Set(uniqueCategories.slice(sliderRange[0], sliderRange[1] + 1)),
        [uniqueCategories, sliderRange],
    );

    const filteredCategoryCount = sliderRange[1] - sliderRange[0] + 1;

    const scrollFilteredData = useMemo(
        () => data.filter((d) => sliderFilteredCategories.has(d[xAxisKey])),
        [data, xAxisKey, sliderFilteredCategories],
    );

    // Memoize all chart configurations
    const { barConfigs, pointConfigs, hasNegativeSales } = useMemo(() => {
        // Prepare data for bar chart (Sales bar on Y2 axis)
        const salesData = scrollFilteredData.map((item) => ({
            ...item,
            value: item.sales,
            metricName: salesBarConfig.label,
            configColor: salesBarConfig.color,
            axisGroup: 'y2',
            dataKey: salesBarConfig.dataKey,
        }));

        const hasNegativeSales = salesData.some(
            (d) => typeof d.value === 'number' && Number.isFinite(d.value) && d.value < 0,
        );

        // Prepare data for price points (on Y1 axis)
        const priceData = scrollFilteredData.flatMap((item) =>
            pricePointConfigs.map((config) => ({
                ...item,
                value: item[config.dataKey],
                metricName: config.label,
                configColor: config.color,
                axisGroup: 'y1',
                dataKey: config.dataKey,
            })),
        );

        // Calculate y min/max for both axes
        let y1MinMax: { min: number; max: number } | undefined;
        let y2MinMax: { min: number; max: number } | undefined;

        if (priceData.length > 0) {
            const y1Values = priceData.map((d) => d.value).filter((v) => v != null && !isNaN(v));
            if (y1Values.length > 0) {
                y1MinMax = {
                    min: Math.min(...y1Values),
                    max: Math.max(...y1Values),
                };
            }
        }

        if (salesData.length > 0) {
            const y2Values = salesData.map((d) => d.value).filter((v) => v != null && !isNaN(v));
            if (y2Values.length > 0) {
                const min = Math.min(...y2Values);
                const max = Math.max(...y2Values);
                const range = max - min || 1;
                const padding = Math.max(range * 0.05, Math.abs(max - min) * 0.05 || 1);
                y2MinMax = {
                    min: min - padding,
                    max: max + padding,
                };
            }
        }

        // Normalize Y2 values to Y1 scale for visual positioning
        let normalizedSalesData = salesData;
        if (y1MinMax && y2MinMax && priceData.length > 0) {
            const y1Range = y1MinMax.max - y1MinMax.min || 1;
            const y2Range = y2MinMax.max - y2MinMax.min || 1;

            normalizedSalesData = salesData.map((d) => {
                if (d.value != null && !isNaN(d.value)) {
                    const normalized = ((d.value - y2MinMax!.min) / y2Range) * y1Range + y1MinMax!.min;
                    return {
                        ...d,
                        value: normalized,
                        originalValue: d.value,
                    };
                }
                return {
                    ...d,
                    originalValue: d.value,
                };
            });
        }

        // Create bar config for Sales (Y2 axis)
        const barConfigs: ChartConfig[] = [];
        const pointConfigs: ChartConfig[] = [];

        if (normalizedSalesData.length > 0) {
            const salesBarChartConfig: ChartConfig = {
                encode: {
                    x: xAxisKey,
                    y: 'value',
                },
                scale: {
                    color: { type: 'identity' },
                    // Limit bar width - prevents bars from filling entire chart when few data points
                    x: { padding: 0.5 },
                },
                style: {
                    fill: salesBarConfig.color,
                    radiusTopLeft: 4,
                    radiusTopRight: 4,
                    radiusBottomLeft: 0,
                    radiusBottomRight: 0,
                },
                tooltip: {
                    title: (d: any) => String(d[xAxisKey] ?? ''),
                    items: [
                        (d: any) => ({
                            name: salesBarConfig.label,
                            value: formatNumWithKMB(Number(d.originalValue ?? d.value ?? 0)),
                            color: salesBarConfig.color,
                        }),
                    ],
                },
                labels: [
                    {
                        text: (d: any) => {
                            const val = d.originalValue !== undefined ? d.originalValue : d.value;
                            return val != null && !isNaN(val) && val >= 0
                                ? formatNumWithKMBTwoDecimalPlaces(val)
                                : '';
                        },
                        position: 'top',
                        textBaseline: 'bottom',
                        dy: -8,
                        fill: 'rgba(0, 0, 0, 0.65)',
                        fontSize: 12,
                        fontFamily: CHART_FONT_FAMILY_CSS,
                        transform: [{ type: 'overlapHide' }],
                    },
                    {
                        text: (d: any) => {
                            const val = d.originalValue !== undefined ? d.originalValue : d.value;
                            return val != null && !isNaN(val) && val < 0
                                ? formatNumWithKMBTwoDecimalPlaces(val)
                                : '';
                        },
                        position: 'bottom',
                        textBaseline: 'top',
                        dy: 8,
                        fill: 'rgba(0, 0, 0, 0.65)',
                        fontSize: 12,
                        fontFamily: CHART_FONT_FAMILY_CSS,
                        transform: [{ type: 'overlapHide' }],
                    },
                ],
                axis: {
                    x: {
                        title: xAxisKey,
                        size: 100,
                        labelAutoWrap: true,
                        labelAutoHide: true,
                        labelFontSize: 13,
                        labelFontFamily: CHART_FONT_FAMILY_CSS,
                        labelStroke: '#000',
                        labelOpacity: 0.4,
                        labelFontWeight: 400,
                        labelFormatter: (text: string) => formatXAxisLabel(text),
                        ...(hasNegativeSales && {
                            crossPadding: 78,
                            labelSpacing: 12,
                        }),
                    },
                    y: {
                        position: 'left',
                        grid: true,
                        title: null,
                        gridStrokeOpacity: 0.2,
                        labelFormatter: (datum: any) => formatNumWithKMB(Number(datum)),
                        labelAutoRotate: false,
                        labelFontSize: 13,
                        labelFontFamily: CHART_FONT_FAMILY_CSS,
                        labelStroke: '#000',
                        labelOpacity: 0.4,
                        labelFontWeight: 400,
                    },
                },
                data: normalizedSalesData,
                additionalConfig: {
                    scrollbar: {
                        x: {
                            ratio: Math.min(1, scrollbarVisibleCount / Math.max(1, filteredCategoryCount)),
                            trackSize: 8,
                            thumbFill: '#5B50D1',
                            thumbFillOpacity: 0.45,
                            trackFill: '#f0f0f0',
                            trackFillOpacity: 1,
                            isRound: true,
                        },
                    },
                },
            };

            barConfigs.push(salesBarChartConfig);

            // Add Y2 axis mark for Sales (right axis) - this is just for the axis display
            if (y2MinMax) {
                const y2AxisConfig: ChartConfig = {
                    encode: {
                        x: xAxisKey,
                        y: 'originalValue',
                    },
                    scale: {
                        y: {
                            independent: true,
                            domain: [y2MinMax.min, y2MinMax.max],
                            nice: true,
                        },
                    },
                    style: {
                        fill: 'transparent',
                        stroke: 'transparent',
                        opacity: 0,
                    },
                    tooltip: false,
                    axis: {
                        y: {
                            position: 'right',
                            grid: false,
                            title: null,
                            gridStrokeOpacity: 0.2,
                            labelFormatter: (datum: any) => {
                                const num = Number(datum);
                                if (num === 0) return '0';
                                return formatNumWithKMB(num);
                            },
                            labelAutoRotate: false,
                            labelFontSize: 13,
                            labelFontFamily: CHART_FONT_FAMILY_CSS,
                            labelStroke: '#000',
                            labelOpacity: 0.4,
                            labelFontWeight: 400,
                        },
                    },
                    data: normalizedSalesData,
                };

                barConfigs.push(y2AxisConfig);
            }
        }

        // Create point configs for price metrics (Y1 axis)
        pricePointConfigs.forEach((config) => {
            const pointData = priceData.filter((d) => d.dataKey === config.dataKey);
            pointConfigs.push({
                encode: {
                    x: xAxisKey,
                    y: 'value',
                },
                scale: {
                    color: { type: 'identity' },
                },
                style: {
                    fill: config.color,
                    stroke: config.color,
                    r: 8,
                },
                axis: {
                    x: {
                        title: false,
                    },
                    y: {
                        title: false,
                        grid: false,
                    },
                },
                tooltip: {
                    title: (d: any) => String(d[xAxisKey] ?? ''),
                    items: [
                        (d: any) => ({
                            name: d.metricName || config.label,
                            value: formatNumWithKMB(Number(d.value)),
                            color: config.color,
                        }),
                    ],
                },
                data: pointData,
            });
        });

        return { barConfigs, pointConfigs, hasNegativeSales };
    }, [
        scrollFilteredData,
        xAxisKey,
        salesBarConfig,
        pricePointConfigs,
        scrollbarVisibleCount,
        filteredCategoryCount,
    ]);

    // Chart container ref
    const chartContainer = useRef<HTMLDivElement | null>(null);
    const chartInstance = useRef<Chart | null>(null);

    // Memoize chart options
    const chartOptions = useMemo(() => {
        const barIDs = barConfigs.map((_, index) => `bar${index + 1}`);
        const pointIDs = pointConfigs.map((_, index) => `point${index + 1}`);
        const options = new BrandPriceLadderChartOptions(scrollFilteredData, barIDs, pointIDs);

        // Set dimension attributes if available
        // insetRight reserves space for Y2 axis labels so they don't overlap with the bars
        // Do not set default height: G2's sizeOf() applies explicit height AFTER reading the
        // container, so a fixed height prevents the plot from matching available area (autoFit).
        // Pass height via sizeAttributes only when a fixed height is intentional.
        const defaultSizeAttributes = {
            paddingTop: 60,
            insetTop: 28,
            paddingBottom: 100 + 35,
            insetRight: 50,
            ...sizeAttributes,
        };
        options.setAttributes(defaultSizeAttributes);

        if (additionalMarks?.length) options.setChildren(additionalMarks);

        // Apply configurations for each bar
        barConfigs.forEach((barConfig, index) => {
            const barKey = `bar${index + 1}`;
            applyConfig(options, barConfig, barKey);
        });

        // Apply configurations for each point
        pointConfigs.forEach((pointConfig, index) => {
            const pointKey = `point${index + 1}`;
            applyConfig(options, pointConfig, pointKey);
        });

        // Set view-level additional props if available
        const defaultViewProps = {
            theme: { fontFamily: CHART_FONT_FAMILY_CSS },
            axis: {
                x: {
                    labelFormatter: (text: string) => formatXAxisLabel(text),
                    title: false,
                    labelFontFamily: CHART_FONT_FAMILY_CSS,
                    ...(hasNegativeSales && {
                        crossPadding: 78,
                        labelSpacing: 12,
                    }),
                },
                y: {
                    labelFormatter: (value: number) => formatNumWithKMB(value),
                    title: false,
                    grid: true,
                    gridLineWidth: 1,
                    gridStroke: 'rgba(0, 0, 0, 0.06)',
                    labelFontFamily: CHART_FONT_FAMILY_CSS,
                },
            },
            legend: false,
            ...viewLevelAdditionalProps,
            interaction: {
                ...getChartColumnHighlightInteraction(),
                ...viewLevelAdditionalProps?.interaction,
                tooltip: {
                    ...getAggregatedChartTooltipInteraction(),
                    ...viewLevelAdditionalProps?.interaction?.tooltip,
                },
            },
            /**
             * G2 scrollbar/slider interactions can merge `clip: options.clip ?? true`, which clips
             * interval labels drawn above bars. Matches EnhancedBarChart / EnhancedStackedBarChart.
             */
            clip: false,
        };
        options.setViewAdditionalProps(defaultViewProps);

        return options.getOptions();
    }, [
        scrollFilteredData,
        barConfigs,
        pointConfigs,
        sizeAttributes,
        viewLevelAdditionalProps,
        additionalMarks,
        hasNegativeSales,
    ]);

    const chartRef = useRef<Chart | null>(null);
    const chartWrapperRef = useRef<HTMLDivElement | null>(null);
    const touchStartRef = useRef<{ x: number; y: number } | null>(null);
    const scrollOffsetRef = useRef(0);
    const lastScrollTimeRef = useRef(0);
    const SCROLL_THROTTLE_MS = 150;

    const needsScroll = filteredCategoryCount > scrollbarVisibleCount;
    const maxScrollOffset = Math.max(0, filteredCategoryCount - scrollbarVisibleCount);

    const scrollToOffset = useCallback(
        (targetOffset: number) => {
            const chart = chartRef.current;
            if (!chart || !needsScroll) return;

            const next = Math.max(0, Math.min(maxScrollOffset, Math.round(targetOffset)));
            const prev = scrollOffsetRef.current;
            if (next === prev) return;
            scrollOffsetRef.current = next;

            const absStart = sliderRange[0] + next;
            const absEnd = Math.min(absStart + scrollbarVisibleCount - 1, sliderRange[1]);
            const startCat = uniqueCategories[absStart];
            const endCat = uniqueCategories[absEnd];

            chart.emit('scrollbarX:filter', {
                data: { selection: [[startCat, endCat], undefined] },
            });
        },
        [needsScroll, maxScrollOffset, scrollbarVisibleCount, sliderRange, uniqueCategories],
    );

    const scrollChart = useCallback(
        (delta: number) => {
            scrollToOffset(scrollOffsetRef.current + delta);
        },
        [scrollToOffset],
    );

    const handleChartReady = useCallback((chart: Chart) => {
        chartRef.current = chart;
        scrollOffsetRef.current = 0;
    }, []);

    useEffect(() => {
        if (!needsScroll) return;
        const wrapper = chartWrapperRef.current;
        if (!wrapper) return;

        const onWheel = (e: WheelEvent) => {
            if (!chartRef.current) return;
            e.preventDefault();
            const now = Date.now();
            if (now - lastScrollTimeRef.current < SCROLL_THROTTLE_MS) return;
            lastScrollTimeRef.current = now;
            const rawDelta = e.deltaX || e.deltaY;
            const direction = rawDelta > 0 ? 1 : -1;
            scrollChart(direction);
        };

        const onTouchStart = (e: TouchEvent) => {
            if (e.touches.length === 1) {
                touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
            }
        };

        const onTouchMove = (e: TouchEvent) => {
            if (!touchStartRef.current || e.touches.length !== 1) return;
            const dx = touchStartRef.current.x - e.touches[0].clientX;
            const dy = touchStartRef.current.y - e.touches[0].clientY;
            if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 8) {
                e.preventDefault();
                const now = Date.now();
                if (now - lastScrollTimeRef.current < SCROLL_THROTTLE_MS) return;
                lastScrollTimeRef.current = now;
                const direction = dx > 0 ? 1 : -1;
                scrollChart(direction);
                touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
            }
        };

        const onTouchEnd = () => {
            touchStartRef.current = null;
        };

        wrapper.addEventListener('wheel', onWheel, { passive: false });
        wrapper.addEventListener('touchstart', onTouchStart, { passive: true });
        wrapper.addEventListener('touchmove', onTouchMove, { passive: false });
        wrapper.addEventListener('touchend', onTouchEnd, { passive: true });

        return () => {
            wrapper.removeEventListener('wheel', onWheel);
            wrapper.removeEventListener('touchstart', onTouchStart);
            wrapper.removeEventListener('touchmove', onTouchMove);
            wrapper.removeEventListener('touchend', onTouchEnd);
        };
    }, [needsScroll, scrollChart]);

    // Render chart
    useEffect(() => {
        if (chartContainer.current) {
            chartInstance.current = new Chart({ container: chartContainer.current });
            if (onBarClick) {
                chartInstance.current.on('interval:click', onBarClick);
            }
            if (onPointClick) {
                chartInstance.current.on('point:click', onPointClick);
            }
            chartInstance.current.options(chartOptions);
            chartInstance.current
                .render()
                .then(() => {
                    // Force layout recalculation after initial render to fix X-axis label wrap/ellipsis
                    chartInstance.current?.forceFit?.();
                    if (chartInstance.current) {
                        handleChartReady(chartInstance.current);
                    }
                })
                .catch((error) => console.error('Render failed', error));
        }

        return () => {
            if (chartInstance.current) {
                if (onBarClick) {
                    chartInstance.current.off('interval:click', onBarClick);
                }
                if (onPointClick) {
                    chartInstance.current.off('point:click', onPointClick);
                }
                chartInstance.current.destroy();
            }
        };
    }, [chartOptions, onBarClick, onPointClick, handleChartReady]);

    // ResizeObserver: G2's autoFit only listens to window resize, not container resize.
    // When the right panel compresses (e.g. layout resize), forceFit() to track container width.
    useEffect(() => {
        const container = chartContainer.current;
        if (!container || typeof ResizeObserver === 'undefined') return;

        let rafId = 0;
        const resizeObserver = new ResizeObserver((entries) => {
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                const chart = chartInstance.current;
                if (!chart) return;
                const entry = entries[0];
                const measuredWidth = entry?.contentRect?.width ?? container.clientWidth;
                if (!measuredWidth) return;
                /**
                 * Use changeSize instead of forceFit because forceFit short-circuits when its
                 * cached _width matches sizeOf(...) — which can happen if the container's
                 * computed size is read mid-layout. changeSize unconditionally re-renders.
                 */
                const currentHeight = (chart.options() as { height?: number })?.height ?? 400;
                chart.changeSize?.(measuredWidth, currentHeight);
            });
        });
        resizeObserver.observe(container);
        return () => {
            cancelAnimationFrame(rafId);
            resizeObserver.disconnect();
        };
    }, []);

    // Build legend items
    const legendItems = [
        { label: salesBarConfig.label, color: salesBarConfig.color },
        ...pricePointConfigs.map((config) => ({ label: config.label, color: config.color })),
    ];

    const chartTheme = useMemo(
        () => ({
            ...theme,
            components: {
                ...(theme as { components?: { Select?: object } })?.components,
                Select: {
                    ...(theme as { components?: { Select?: object } })?.components?.Select,
                    colorTextPlaceholder: 'rgba(0,0,0,0.7)',
                },
            },
        }),
        [theme],
    );

    return (
        <ConfigProvider theme={chartTheme}>
            <div
                className={`w-full bg-white rounded-lg border border-[rgba(0,0,0,0.06)] ${className}`}
                data-test={props['data-test']}
                data-test-id={props['data-test-id']}
                data-asper-brand-price-ladder
            >
                {/* Header Section */}
                <div className="p-5 border-b border-[rgba(0,0,0,0.06)]">
                    <div className="flex justify-between items-start mb-2">
                        <h3 className="font-['Open_Sans'] font-semibold text-[16px] leading-normal text-[rgba(0,0,0,0.88)] m-0">
                            {title || propTitle}
                        </h3>
                        {onDownload && (
                            <Button
                                type="text"
                                icon={<DownloadFile size={16} />}
                                onClick={onDownload}
                                className="!p-2"
                                data-test-id={`${props['data-test-id']}-download-btn`}
                            />
                        )}
                    </div>

                    {description && (
                        <p className="font-['Open_Sans'] font-normal text-[14px] leading-[22px] text-[rgba(0,0,0,0.65)] m-0">
                            {description}
                        </p>
                    )}
                </div>

                {/* Filters Section - same logic and fonts as EnhancedBarChart */}
                {(filters.length > 0 || (vizData && xFilterOptions.length > 0)) && (
                    <div className="px-5 pt-5 pb-3" data-export-exclude>
                        <ChartFilterBar>
                            {/* X-axis filter (auto-generated from vizData, same as bar chart) */}
                            {vizData && xFilterOptions.length > 0 && (
                                <ChartFilterColumn
                                    label={(() => {
                                        const rawTitle = vizData.xAxisTitle?.[0] ?? (vizData as any).xAxisTitles?.[0];
                                        if (rawTitle && String(rawTitle).toLowerCase() !== 'x-axis filter') {
                                            return rawTitle;
                                        }
                                        return 'X-Axis';
                                    })()}
                                >
                                    <Select
                                        mode="multiple"
                                        value={xFilter}
                                        onChange={(value) =>
                                            setXFilter(Array.isArray(value) ? value : value != null ? [value] : [])
                                        }
                                        options={xFilterOptions.map((x) => ({
                                            label: x,
                                            value: x,
                                        }))}
                                        tagRender={chartMultiSelectTagRender(xFilter, (v) => v)}
                                        className={CHART_FILTER_SELECT_CLASSNAME}
                                        placeholder="Select..."
                                        data-test-id={`${props['data-test-id']}-filter-x`}
                                    />
                                </ChartFilterColumn>
                            )}
                            {/* Custom filters from props */}
                            {filters.map((filter, index) => (
                                <ChartFilterColumn key={`filter-${index}`} label={filter.label}>
                                    <Select
                                        value={filter.defaultValue}
                                        onChange={filter.onChange}
                                        options={filter.options}
                                        className={CHART_FILTER_SELECT_CLASSNAME}
                                        data-test-id={`${props['data-test-id']}-filter-${index}`}
                                    />
                                </ChartFilterColumn>
                            ))}
                        </ChartFilterBar>
                    </div>
                )}

                {/* Legend Section */}
                {legendItems.length > 0 && (
                    <div className="px-5 py-3 w-full min-w-0">
                        <div className="flex justify-center gap-4 flex-wrap items-center max-w-full">
                            {legendItems.map((item, index) => (
                                <div key={`legend-${index}`} className="flex items-center gap-2 min-w-0 max-w-full">
                                    <div
                                        className={`w-3 h-3 shrink-0 ${index === 0 ? 'rounded-sm' : 'rounded-full'}`}
                                        style={{ backgroundColor: item.color }}
                                    />
                                    <span className="font-['Open_Sans'] font-normal text-[14px] leading-[22px] text-[rgba(0,0,0,0.65)] break-words">
                                        {item.label}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Y-Axis Labels */}
                {(y1AxisLabel || y2AxisLabel) && (
                    <div className="px-5 pt-3">
                        <div className="flex justify-between">
                            {y1AxisLabel && (
                                <p className="font-['Open_Sans'] font-normal text-sm leading-[20px] text-[rgba(0,0,0,0.45)] m-0">
                                    {y1AxisLabel}
                                </p>
                            )}
                            {y2AxisLabel && (
                                <p className="font-['Open_Sans'] font-normal text-sm leading-[20px] text-[rgba(0,0,0,0.45)] m-0">
                                    {y2AxisLabel}
                                </p>
                            )}
                        </div>
                    </div>
                )}

                {/* Chart Section — x-axis uses G2 scrollbar (categorical), with wheel/touch scroll like EnhancedBarChart */}
                <div ref={chartWrapperRef} className={`px-5 py-5 ${chartClassName}`}>
                    <div
                        ref={chartContainer}
                        className="w-full min-w-0"
                        data-test={`${props['data-test']}-chart`}
                        data-asper-brand-price-ladder-chart
                    />
                </div>

                {/* Footer Section */}
                {footerLabel && (
                    <div className="px-5 pb-5 flex justify-center items-center gap-2">
                        <span className="font-['Open_Sans'] font-semibold text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)]">
                            {footerLabel}
                        </span>
                        {showFooterArrow && <ArrowRight size={16} color="rgba(0,0,0,0.88)" />}
                    </div>
                )}
            </div>
        </ConfigProvider>
    );
};

export default BrandPriceLadder;
